import * as React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/popover';
import { Tooltip } from '@heroui/tooltip';
import { LightbulbFilamentIcon, BooksIcon, XCircleIcon, StarIcon, SealQuestionIcon } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

// Assistant SVG component with customizable color
const AssistantIcon: React.FC<{
  className?: string;
}> = ({
  className = ''
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" className={className} data-sentry-element="svg" data-sentry-component="AssistantIcon" data-sentry-source-file="prompt-library.tsx">
    <path fill="currentColor" d="M8.353 1.591c-3.75 0-6.8 3.05-6.8 6.8 0 3.751 3.05 6.8 6.8 6.8a6.756 6.756 0 0 0 3.975-1.293l5.26 5.259 1.13-1.131-5.21-5.211a6.764 6.764 0 0 0 1.645-4.424c0-3.75-3.049-6.8-6.8-6.8Zm0 .8c3.319 0 6 2.682 6 6 0 3.319-2.681 6-6 6s-6-2.681-6-6c0-3.318 2.681-6 6-6ZM8.4 4c-1.384 0-2.74.686-3.583 1.752a.4.4 0 1 0 .628.496C6.128 5.384 7.287 4.8 8.4 4.8a.4.4 0 1 0 0-.8Zm1.602.337a.4.4 0 0 0-.16.77C11.061 5.627 12 6.752 12 8v2a.398.398 0 0 0 .554.375A.4.4 0 0 0 12.8 10V8c0-1.638-1.18-3.002-2.642-3.629a.4.4 0 0 0-.156-.034ZM8.4 5.6c-.66 0-1.33.255-1.861.671C6.008 6.687 5.6 7.291 5.6 8v3.6a.4.4 0 1 0 .8 0V8c0-.396.24-.792.633-1.1.392-.307.923-.5 1.367-.5.444 0 .975.193 1.367.5.393.308.633.704.633 1.1a.398.398 0 0 0 .554.375A.4.4 0 0 0 11.2 8c0-.709-.408-1.313-.94-1.729-.53-.416-1.2-.671-1.86-.671ZM4.394 7.595A.4.4 0 0 0 4 8v2.4a.4.4 0 1 0 .8 0V8a.4.4 0 0 0-.406-.405ZM8.4 7.6c-.658 0-1.2.542-1.2 1.2a.4.4 0 1 0 .8 0c0-.226.174-.4.4-.4.226 0 .4.174.4.4V10a.4.4 0 1 0 .8 0V8.8c0-.658-.542-1.2-1.2-1.2Zm2.394 1.995A.4.4 0 0 0 10.4 10v1.2a.398.398 0 0 0 .554.375.4.4 0 0 0 .246-.375V10a.4.4 0 0 0-.406-.405Zm-3.2.4a.4.4 0 0 0-.394.405V12a.4.4 0 1 0 .8 0v-1.6a.4.4 0 0 0-.406-.405Zm1.6 1.2a.4.4 0 0 0-.394.405v.8a.4.4 0 1 0 .8 0v-.8a.4.4 0 0 0-.406-.405Z" data-sentry-element="path" data-sentry-source-file="prompt-library.tsx" />
  </svg>;
export interface Prompt {
  Category: string;
  Prompt: string;
  Purpose: string;
  ShortPrompt: string;
  SubCategory: string;
}
export interface PromptLibraryProps {
  featureFlags?: {
    [key: string]: any;
  };
  onPromptSelect: (query: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  triggerElement?: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
  className?: string;
  popoverRef?: React.RefObject<HTMLDivElement>;
  isOpen?: boolean;
}
export const PromptLibrary: React.FC<PromptLibraryProps> = ({
  featureFlags,
  onPromptSelect,
  onOpenChange,
  triggerElement,
  placement = 'bottom',
  className = '',
  popoverRef,
  isOpen: externalIsOpen
}) => {
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalIsOpen !== undefined ? onOpenChange || (() => {}) : setInternalIsOpen;
  const [selectedCategory, setSelectedCategory] = React.useState<string>('All Keywords');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  // Notify parent component when open state changes
  React.useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Extract prompts from featureFlags.promptLibrary and ensure they match the Prompt interface
  const prompts: Prompt[] = Array.isArray(featureFlags?.promptLibrary) ? featureFlags.promptLibrary.filter((p: any) => typeof p.Prompt === 'string' && typeof p.ShortPrompt === 'string' && typeof p.Category === 'string' && typeof p.Purpose === 'string' && typeof p.SubCategory === 'string') : [];

  // Get unique categories and subcategories for the dropdown
  const categories = React.useMemo(() => {
    const categoryMap = new Map<string, Set<string>>();
    prompts.forEach(prompt => {
      if (!categoryMap.has(prompt.Category)) {
        categoryMap.set(prompt.Category, new Set());
      }
      categoryMap.get(prompt.Category)!.add(prompt.SubCategory);
    });
    return categoryMap;
  }, [prompts]);

  // Filter prompts based on selected category/subcategory and search query
  const filteredPrompts = React.useMemo(() => {
    let filtered = prompts;
    if (selectedCategory !== 'All Keywords') {
      if (selectedCategory.includes(' > ')) {
        // Subcategory selected
        const [category, subcategory] = selectedCategory.replace('  ', '').split(' > ');
        filtered = prompts.filter(prompt => prompt.Category === category && prompt.SubCategory === subcategory);
      } else {
        // Main category selected
        filtered = prompts.filter(prompt => prompt.Category === selectedCategory);
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(prompt => prompt.ShortPrompt.toLowerCase().includes(query) || prompt.Prompt.toLowerCase().includes(query) || prompt.Category.toLowerCase().includes(query) || prompt.SubCategory.toLowerCase().includes(query));
    }

    // Sort alphabetically by ShortPrompt
    filtered = [...filtered].sort((a, b) => {
      const comparison = a.ShortPrompt.localeCompare(b.ShortPrompt);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return filtered;
  }, [prompts, selectedCategory, sortDirection, searchQuery]);
  const handlePromptClick = (query: string) => {
    onPromptSelect(query);
    setIsOpen(false);
  };
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const clearSearch = () => {
    setSearchQuery('');
  };
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All Keywords');
  };
  const defaultTrigger = <button type="button" id="prompt-library-open-button" aria-label="Prompt Library" aria-haspopup="dialog" className="flex flex-row items-center gap-2 p-2 rounded-full border border-brand-blue-light/60 hover:border-brand-blue-light/100 bg-white/5 hover:bg-white/10 cursor-pointer">
      <LightbulbFilamentIcon size={20} weight="duotone" className="text-white/65" />
      <span className="text-white/65 text-sm">Prompt Library</span>
    </button>;
  return <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement={placement} showArrow={false} offset={70} triggerScaleOnOpen={false} shouldCloseOnBlur={false} classNames={{
    content: 'flex flex-col justify-start items-start bg-white shadow-none drop-shadow-none text-black p-4 rounded-md shadow-lg border border-gray-200 w-full sm:w-[50vw] h-[90vh] sm:h-[80vh] ml-[-8px]',
    base: 'before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0 before:bg-white shadow-none drop-shadow-none'
  }} data-sentry-element="Popover" data-sentry-component="PromptLibrary" data-sentry-source-file="prompt-library.tsx">
      <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="prompt-library.tsx">
        {triggerElement || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent data-sentry-element="PopoverContent" data-sentry-source-file="prompt-library.tsx">
          <div ref={popoverRef} className="flex flex-col justify-start items-start w-full h-full overflow-hidden">
            <div className="flex flex-row w-full items-center gap-2 pb-2 mb-1 sm:mb-4 border-b-1 border-solid border-gray-200">
              <BooksIcon size={30} weight="duotone" className="text-brand-blue-light" data-sentry-element="BooksIcon" data-sentry-source-file="prompt-library.tsx" />
              <h3 className="text-2xl font-semibold text-gray-900 m-0 p-0">Prompt Library</h3>
            </div>
          
          <div className="flex flex-row items-center justify-center w-full py-2 sm:py-4">
            <div className="flex flex-wrap gap-1 sm:gap-4 justify-center items-center bg-brand-blue-light/10 rounded-lg p-2 sm:p-4 w-full sm:w-fit relative">
              {/* Search Field */}
                <span className="text-sm font-medium text-gray-700">Search:</span>
                <div className="flex flex-row relative">
                  <input type="text" id="promt-library-search-input" value={searchQuery} onChange={handleSearchChange} className="max-w-[200px] border-1 border-solid border-gray-200 sm:p-1.5 min-w-[200px] rounded-lg text-sm text-gray-900 focus:outline-none outline-none" />
                  {searchQuery && <button id="promt-library-search-clear-button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-1 flex items-center text-gray-400 hover:text-gray-600 hover:cursor-pointer">
                      <XCircleIcon size={24} weight="fill" className="text-gray-300 hover:text-gray-400 hover:cursor-pointer" />
                    </button>}
                </div>
              {/* Category Filter Dropdown */}
                <span className="text-sm font-medium text-gray-700 sm:flex hidden">Filter by Keyword:</span>
                <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                  <select value={selectedCategory} id="promt-library-category-select" onChange={e => setSelectedCategory(e.target.value)} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} onFocus={e => e.stopPropagation()} className="sm:flex hidden flex-row gap-2 items-center justify-between sm:p-2 min-w-full sm:min-w-[200px] text-left bg-white border-1 border-solid border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none outline-none">
                    <option value="All Keywords">All Keywords</option>
                    {Array.from(categories.keys()).sort().map(category => <optgroup key={category}>
                        <option value={category}>{category}</option>
                        {Array.from(categories.get(category)!).sort().map(subcategory => {
                    // Don't show subcategories for Suggested Prompt categories
                    if (category === 'Suggested Prompt' || category === 'Suggested Prompts') {
                      return null;
                    }
                    return <option key={`${category} > ${subcategory}`} value={`${category} > ${subcategory}`}>
                              &nbsp;&nbsp;&nbsp;&nbsp;{subcategory}
                            </option>;
                  })}
                      </optgroup>)}
                  </select>
                </div>
                
                {/* Sort Direction Toggle
                 <div className="flex flex-row items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Sort Alphabetically:</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="flex items-center justify-center p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-blue-light/60"
                    title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    {sortDirection === 'asc' ? (
                      <SortAscendingIcon size={25} className="text-gray-600" />
                    ) : (
                      <SortDescendingIcon size={25} className="text-gray-600" />
                    )}
                  </button>
                 </div> */}
              </div>
            </div>
          
          {filteredPrompts.length === 0 ? <div className="text-gray-500 text-sm sm:py-4 text-center w-full">
              {prompts.length === 0 ? 'No prompts available' : searchQuery ? `No prompts found matching "${searchQuery}"` : `No prompts found in "${selectedCategory}"`}
            </div> : <>
              <div className="box-border flex flex-wrap gap-4 overflow-y-auto scrollbar-hide sm:scrollbar-default custom-scrollbar3 justify-start sm:justify-center items-start w-full sm:p-4">
                {filteredPrompts.map((prompt: Prompt, index: number) => <motion.div key={index} whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 25px rgba(56, 189, 248, 0.15)"
            }} transition={{
              type: "spring",
              stiffness: 300,
              damping: 20
            }} onClick={e => {
              // Only trigger if the click is not on the icon area
              if (!(e.target as Element).closest('.assistant-icon-container')) {
                handlePromptClick(prompt.Prompt);
              }
            }} className="flex flex-col justify-between max-w-full sm:max-w-[250px] sm:min-h-[140px] items-start p-3 text-left border-2 border-solid border-gray-200 hover:border-brand-blue-light/50
                      rounded-lg bg-gray-100 hover:bg-white transition-colors duration-200 group cursor-pointer overflow-y-auto">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-base font-semibold leading-tight tracking-normal text-gray-900 transition-colors duration-200 line-clamp-2 flex-1">
                          {prompt.ShortPrompt}
                        </span>
                        <Tooltip content={<div className="flex flex-col gap-2">
                              <span className="font-bold">Full Prompt:</span> 
                              <span className="text-sm text-gray-200">{prompt.Prompt}</span>
                            </div>} placement="top" delay={0} offset={5} showArrow={true} classNames={{
                    content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                    base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
                  }}>
                          <SealQuestionIcon size={20} weight="fill" className="text-gray-400 hover:text-brand-blue cursor-help flex-shrink-0" />
                        </Tooltip>
                      </div>
                      <span className="text-sm italic text-gray-600 line-clamp-2">
                        {prompt.Prompt}
                      </span>
                    </div>
                    <div className="flex flex-row justify-between gap-2 w-full items-end">
                      <div className="flex flex-row gap-2 items-center">
                        <div className="text-xs text-black bg-black/15 rounded-md px-2 py-1 flex flex-row items-center justify-center gap-1">
                          {(prompt.Category === 'Suggested Prompt' || prompt.Category === 'Suggested Prompts') && <StarIcon size={12} weight="fill" className="text-brand-blue" />}
                          <span className="text-xs text-black whitespace-nowrap">{prompt.Category}</span>
                        </div>
                        <span className="text-xs text-black bg-black/15 rounded-md px-2 py-1">
                          {prompt.SubCategory}
                        </span>
                      </div>
                      {/* <span className="text-xs text-gray-500 mt-1 bg-white/50 border border-gray-200 rounded-md px-2 py-1">
                        {prompt.Purpose}
                       </span> */}
                      <Tooltip content="Use this prompt." placement="top" delay={1000} offset={5} showArrow={true} classNames={{
                  content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                  base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
                }}>
                        <div role="button" tabIndex={0} aria-label={`Use prompt: ${prompt.Prompt.substring(0, 50)}${prompt.Prompt.length > 50 ? '...' : ''}`} onClick={e => {
                    e.stopPropagation();
                    handlePromptClick(prompt.Prompt);
                  }} onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePromptClick(prompt.Prompt);
                    }
                  }} className="flex cursor-pointer assistant-icon-container justify-end items-end">
                          <AssistantIcon className="w-[20px] h-[20px] rounded-full p-1  text-gray-100 group-hover:text-white transition-all duration-200 group-hover:bg-brand-blue/70 hover:bg-black hover:text-white" aria-hidden="true" />
                        </div>
                      </Tooltip>
                    </div>
                  </motion.div>)}
              </div>
              
              {/* Reset Filters Button */}
              {(searchQuery || selectedCategory !== 'All Keywords') && <div className="flex justify-center w-full pt-4">
                  <button onClick={resetFilters} aria-label="Reset filters" className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-300 text-blue-700 rounded-lg transition-colors duration-200 text-sm hover:cursor-pointer">
                    <span>Reset Filters</span>
                  </button>
                </div>}
            </>}
          </div>
      </PopoverContent>
    </Popover>;
};